import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Target, Eye, HeartHandshake, Trophy } from "lucide-react";
import { COMPANY, ADVISOR, WHY_CHOOSE, CREDENTIALS } from "../data/mock";
import { useRevealGroup, useCounter } from "../hooks/useReveal";
import { PageHero, SectionHeading, PillBadge, TestimonialsSection, CTASection } from "../components/Shared";

function Milestone({ value, suffix, label, index }) {
  const [ref, v] = useCounter(value);
  return (
    <div ref={ref} data-reveal style={{ transitionDelay: `${index * 90}ms` }} className="rounded-2xl bg-white p-8 text-center shadow-[0_2px_16px_rgba(0,111,185,0.07)]">
      <p className="font-heading text-4xl font-extrabold text-[#006FB9]">{v}<span className="text-[#54AEDB]">{suffix}</span></p>
      <p className="mt-2 text-sm font-medium text-[#526A7D]">{label}</p>
    </div>
  );
}

export default function About() {
  const ref = useRevealGroup();

  return (
    <div ref={ref}>
      <PageHero
        badge="About Us"
        title="Because Your Peace Matters"
        subtitle={`Since ${COMPANY.since}, Abhi Insurance Agency has been helping Canadians, Canadian immigrants and visitors with all their insurance needs.`}
        image="https://images.pexels.com/photos/7938055/pexels-photo-7938055.jpeg"
      />

      {/* Story */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div data-reveal>
            <PillBadge>Our Story</PillBadge>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Prepared for the <span className="text-[#0785C7]">Unexpected</span>, Since {COMPANY.since}
            </h2>
            <div className="mt-6 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              <p>
                Life is full of challenges and unexpected events. Before anything unpredictable or unlucky occurs, protect yourself and your family from financial loss with the right insurance coverage.
              </p>
              <p>
                Abhi Insurance Agency meets all your insurance needs — life insurance, visitor visa insurance, disability insurance, and so much more. As your personal insurance advisor in Surrey, BC, we help you navigate the unexpected and prepare for the worst, taking the worry and guesswork out of your financial protection.
              </p>
              <p>
                Purchasing insurance can feel daunting. Many people forego protection simply because they don't know what coverage they need, how much to spend, or where to go for support. There's no need to take this risk — we help you navigate your insurance needs based on your current circumstances and long-term goals.
              </p>
              <p className="font-semibold text-[#193B58]">
                We guarantee you the lowest possible premiums, with up to 45% discounted rates. Your 100% satisfaction is guaranteed.
              </p>
            </div>
          </div>
          <div className="relative" data-reveal style={{ transitionDelay: "120ms" }}>
            <div className="overflow-hidden rounded-3xl">
              <img src="https://images.pexels.com/photos/8317703/pexels-photo-8317703.jpeg" alt="Multigenerational family" className="h-[520px] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="float-slow absolute -bottom-7 left-6 rounded-2xl bg-[#006FB9] p-6 text-white shadow-[0_20px_50px_rgba(0,111,185,0.35)]">
              <p className="font-heading text-3xl font-extrabold">100%</p>
              <p className="mt-1 max-w-[150px] text-sm leading-snug text-[#C5DEEC]">Satisfaction Guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="bg-[#F4F7FB] py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading badge="What Drives Us" title={<>Our Mission & <span className="text-[#0785C7]">Values</span></>} />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { icon: Target, title: "Our Mission", text: "To take the worry and guesswork out of financial protection — comparing plans across companies so every client is confident they have the best coverage for their needs and budget." },
              { icon: Eye, title: "Our Vision", text: "To be the most trusted personal insurance advisory in British Columbia for Canadians, newcomers and visitors alike — known for honesty, patience and results." },
              { icon: HeartHandshake, title: "Our Values", text: "Transparency with no hidden fees, tailored advice over one-size-fits-all sales, and availability 7 days a week — because emergencies don't keep business hours." },
            ].map((v, i) => (
              <div key={v.title} data-reveal style={{ transitionDelay: `${i * 90}ms` }} className="group rounded-2xl bg-white p-9 shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(0,111,185,0.13)]">
                <span className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#006FB9]">
                  <v.icon className="h-6 w-6 text-[#006FB9] transition-colors duration-500 group-hover:text-[#B9DFF3]" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#0B2748]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#526A7D]">{v.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 lg:grid-cols-4">
            <Milestone value={25} suffix="+" label="Years of Trusted Service" index={0} />
            <Milestone value={45} suffix="%" label="Premium Savings — Up To" index={1} />
            <Milestone value={8} suffix="x" label="'Top of the Table' Designation" index={2} />
            <Milestone value={7} suffix="" label="Days a Week Availability" index={3} />
          </div>
        </div>
      </section>

      {/* Advisor */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div className="relative order-2 lg:order-1" data-reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={ADVISOR.image} alt={ADVISOR.name} className="h-[500px] w-full object-cover object-top transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 p-5 shadow-lg backdrop-blur">
              <p className="font-heading text-lg font-bold text-[#0B2748]">{ADVISOR.name}</p>
              <p className="text-sm text-[#526A7D]">{ADVISOR.role}</p>
            </div>
          </div>
          <div className="order-1 lg:order-2" data-reveal style={{ transitionDelay: "120ms" }}>
            <PillBadge>Leadership</PillBadge>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Owned & Managed by <span className="text-[#0785C7]">{ADVISOR.name}</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">{ADVISOR.bio}</p>
            <ul className="mt-7 space-y-3">
              {CREDENTIALS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-[15px] font-medium text-[#193B58]">
                  <Trophy className="h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 text-[#0785C7]" /> {c}
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-primary group mt-9" data-testid="about-book-consult">
              Book a Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="bg-[#F4F7FB] py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading badge="Our Promise" title={<>Your Satisfaction is <span className="text-[#0785C7]">100% Guaranteed</span></>} />
          <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2">
            {WHY_CHOOSE.map((w, i) => (
              <div key={w.title} data-reveal style={{ transitionDelay: `${i * 60}ms` }} className="flex items-start gap-3.5 rounded-2xl bg-white p-6 shadow-sm transition-all duration-400 hover:shadow-[0_14px_34px_rgba(0,111,185,0.11)]">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" />
                <div>
                  <p className="font-heading text-[15px] font-bold text-[#0B2748]">{w.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#526A7D]">{w.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <CTASection title="Let's Protect What You've Built" />
    </div>
  );
}
